import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cardinal, AnimationState, DrawHistory as DrawHistoryType } from '../types';
import CardReveal from './CardReveal';
import { ArrowPathIcon, ShareIcon, EnvelopeIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';

interface PackOpenerProps {
  onCardSelected: (cardinal: Cardinal) => void;
  cardinaux: Cardinal[];
  drawHistory?: DrawHistoryType[];
}

const PackOpener: React.FC<PackOpenerProps> = ({ onCardSelected, cardinaux, drawHistory = [] }) => {
  const navigate = useNavigate();
  const [animationState, setAnimationState] = useState<AnimationState>('idle');
  const [selectedCardinal, setSelectedCardinal] = useState<Cardinal | null>(null);
  const [cardPosition, setCardPosition] = useState(0);
  
  // Animation pour faire défiler les cartes rapidement
  useEffect(() => {
    if (animationState === 'opening') {
      const interval = setInterval(() => {
        setCardPosition(prev => (prev + 1) % cardinaux.length);
      }, 100);
      
      // Arrêter l'animation après 2 secondes et révéler la carte
      const timeout = setTimeout(() => {
        clearInterval(interval);
        
        // Sélectionner spécifiquement le cardinal Robert Francis Prevost
        const prevostCardinal = cardinaux.find(cardinal => 
          cardinal.name.toLowerCase().includes('prevost') || 
          cardinal.name.toLowerCase().includes('robert francis') ||
          cardinal.name.toLowerCase().includes('robert f.')
        );
        
        // Si Prevost est trouvé, l'utiliser, sinon utiliser le premier cardinal de la liste
        const cardinal = prevostCardinal || cardinaux[0];
        
        setSelectedCardinal(cardinal);
        setAnimationState('reveal');
        
        // Notifier le parent du cardinal sélectionné
        onCardSelected(cardinal);
      }, 2000);
      
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [animationState, cardinaux, onCardSelected]);
  
  const handleOpenPack = () => {
    if (animationState === 'idle') {
      setAnimationState('opening');
    }
  };
  
  const handleReset = () => {
    setAnimationState('idle');
    setSelectedCardinal(null);
  };

  const handleHistoryCardClick = (cardinal: Cardinal) => {
    setSelectedCardinal(cardinal);
  };

  return (
    <div className="flex flex-col items-center justify-center mx-auto max-w-4xl p-4 sm:p-6">
      <AnimatePresence mode="wait">
        {animationState === 'idle' && (
          <motion.div
            key="idle"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="text-center"
          >
            <motion.div
              className="relative mx-auto mb-8 sm:mb-8 w-44 h-44 sm:w-64 sm:h-64 flex items-center justify-center rounded-full"
              animate={{ 
                boxShadow: "0 0 30px 10px rgba(255, 215, 0, 0.3)",
              }}
              transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
            >
              <motion.div
                className="absolute w-full h-full rounded-full bg-gradient-to-br from-yellow-400 to-red-600 opacity-50"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
              <motion.div
                className="absolute w-2/4 h-2/4 rounded-full bg-gradient-to-tl  from-yellow-800 to-white opacity-70 "
                animate={{ rotate: -360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              />
              <motion.div 
                className="absolute w-1/2 h-1/2 rounded-full bg-red-800 flex items-center justify-center z-10"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleOpenPack}
              >
                <img 
                  src="/—Pngtree—hands folded hand drawn prayer_5424863.png" 
                  alt="Mains en prière" 
                  className="w-14 h-14 sm:w-20 sm:h-20 object-contain"
                />
              </motion.div>
            </motion.div>
            
            <motion.button
              className="bg-yellow-900 text-white text-base sm:text-lg font-bold px-5 py-3 sm:px-8 sm:py-4 rounded-full"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleOpenPack}
            >
              <span className="flex items-center">
                Qui est Léon XIV ?
              </span>
            </motion.button>
            
          </motion.div>
        )}
        
        {animationState === 'opening' && (
          <motion.div
            key="opening"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center"
          >
            <div className="w-72 h-96 mx-auto relative perspective">
              <motion.div 
                className="card absolute inset-0 bg-gradient-to-br from-red-800 to-yellow-500 rounded-lg shadow-xl"
                animate={{ 
                  rotateY: [0, 180, 0, 180, 0],
                  scale: [1, 1.05, 1, 1.05, 1],
                  boxShadow: [
                    "0 0 10px 0px rgba(255, 215, 0, 0.3)",
                    "0 0 20px 5px rgba(255, 215, 0, 0.5)",
                    "0 0 10px 0px rgba(255, 215, 0, 0.3)",
                    "0 0 20px 5px rgba(255, 215, 0, 0.5)",
                    "0 0 10px 0px rgba(255, 215, 0, 0.3)"
                  ]
                }}
                transition={{ duration: 2, ease: "easeInOut" }}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  {cardinaux[cardPosition]?.imageUrl ? (
                    <img 
                      src={cardinaux[cardPosition].imageUrl || ''}
                      alt="Cardinal"
                      className="w-32 h-32 object-cover rounded-full border-4 border-yellow-500 opacity-70"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full border-4 border-yellow-500 bg-gray-700 opacity-70" />
                  )}
                </div>

                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-600 opacity-20"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  style={{ borderRadius: '0.5rem' }}
                />
              </motion.div>
            </div>
            <motion.p 
              className="mt-7 text-gray-200 text-lg font-semibold"
              animate={{ 
                opacity: [0.5, 1, 0.5],
                scale: [0.98, 1.02, 0.98]
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              Que le Saint-Esprit vous guide...
            </motion.p>
            <motion.div
              className="mt-2 flex justify-center space-x-1"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              {[0, 1, 2].map(i => (
                <motion.div 
                  key={i}
                  className="w-2 h-2 bg-yellow-500 rounded-full"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ 
                    duration: 0.6, 
                    repeat: Infinity, 
                    delay: i * 0.2 
                  }}
                />
              ))}
            </motion.div>
          </motion.div>
        )}
        
        {animationState === 'reveal' && selectedCardinal && (
          <motion.div
            key="reveal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center"
          >
            <CardReveal data={selectedCardinal} isRevealed={true} />
            
            <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6">
              <motion.button
                className="btn-primary text-lg font-bold px-8 py-4 rounded-full"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/formulaire')}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
              >
                <span className="flex items-center">
                  <EnvelopeIcon className="w-6 h-6 mr-2 text-yellow-500" />
                  Recevoir par email
                </span>
              </motion.button>

              <motion.button
                className="btn-secondary text-lg font-bold px-8 py-4 rounded-full"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  // Créer le texte à partager
                  const shareText = `J'ai pioché le cardinal ${selectedCardinal.name} pour le conclave ! Priez aussi pour votre cardinal sur le site https://participez-au-conclave.fr/`;
                  
                  // Vérifier si l'API Web Share est disponible
                  if (navigator.share) {
                    navigator.share({
                      title: 'Adopte un Cardinal',
                      text: shareText,
                      url: 'https://participez-au-conclave.fr/',
                    })
                    .catch((error) => console.log('Erreur de partage', error));
                  } else {
                    // Fallback pour les navigateurs qui ne supportent pas l'API Web Share
                    try {
                      navigator.clipboard.writeText(shareText);
                      alert('Texte copié dans le presse-papier ! Vous pouvez maintenant le coller sur vos réseaux sociaux.');
                    } catch (err) {
                      console.error('Impossible de copier le texte: ', err);
                    }
                  }
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
              >
                <span className="flex items-center">
                  <ShareIcon className="w-6 h-6 mr-2 text-yellow-500" />
                  Partager
                </span>
              </motion.button>
            </div>

            {/* Section de prière */}
            <motion.div
              className="mt-8 p-4 sm:p-6 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-xl max-w-2xl mx-4 sm:mx-auto border border-gray-700 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
            >
              <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 mb-4">
                Prière pour le Conclave
              </h3>
              <div className="text-gray-300 text-sm leading-relaxed italic">
                <p className="mb-4">
                  Dieu, père miséricordieux,<br />
                  qui as révélé ton amour dans ton Fils Jesus Christ,<br />
                  et l'as répandu sur nous dans l'Esprit Saint Consolateur,<br />
                  nous te confions aujourd'hui le destin du monde et de chaque homme.<br />
                  Penche-toi sur nos péchés, guéris notre faiblesse, vaincs tout mal,<br />
                  fais que tous les habitants de la terre fassent l'expérience de ta miséricorde,<br />
                  afin qu'en toi, Dieu un et trine, ils trouvent la source de l'espérance.<br />
                  Nous te confions particulièrement notre <span className="font-bold text-yellow-500">Pape Léon XIV</span>,<br />
                  guide-le par ton Esprit Saint dans sa mission au service de l'Église,<br />
                  qu'il soit uni à ses frères Cardinaux pour qu'ensemble <br />
                  ils choisissent le pasteur dont l'Eglise et notre monde ont besoin.<br />
                  Par notre Seigneur Jésus‑Christ,<br />
                  qui vit et règne avec toi dans l'unité du Saint‑Esprit,<br />
                  pour les siècles des siècles. Amen.
                </p>
              </div>
            </motion.div>

            {/* Journal des tirages */}
            {drawHistory && drawHistory.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="mt-12 p-4 sm:p-6 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-xl max-w-md mx-auto border border-gray-700 w-full"
              >
                <div className="flex items-center justify-center mb-6">
                  <motion.div
                    className="w-10 h-10 mr-3 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <svg 
                      className="w-6 h-6 text-white" 
                      fill="currentColor" 
                      viewBox="0 0 20 20" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path 
                        fillRule="evenodd" 
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" 
                        clipRule="evenodd" 
                      />
                    </svg>
                  </motion.div>
                  <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
                    Historique
                  </h2>
                </div>

                <div className="max-h-72 overflow-y-auto pr-2 custom-scrollbar">
                  {drawHistory.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.3 }}
                      className="mb-4 p-4 bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg flex items-center border-l-4 border-yellow-500 hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => handleHistoryCardClick(item.cardinal)}
                    >
                      <div className="w-14 h-14 mr-4 rounded-full overflow-hidden flex-shrink-0 border-2 border-yellow-500 shadow-md relative">
                        {item.cardinal.imageUrl ? (
                          <img
                            src={item.cardinal.imageUrl}
                            alt={item.cardinal.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/No-image-available.png/200px-No-image-available.png";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-red-900 to-yellow-600 flex items-center justify-center text-white text-xs font-bold">
                            {item.cardinal.name.substring(0, 2).toUpperCase()}
                          </div>
                        )}
                        <motion.div 
                          className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-800 opacity-0 hover:opacity-20 transition-opacity"
                        />
                      </div>
                      <div className="flex-grow">
                        <div className="text-white font-semibold text-lg">{item.cardinal.name}</div>
                        <div className="text-gray-300 text-xs flex items-center">
                          <svg 
                            className="w-3 h-3 mr-1 text-yellow-500" 
                            fill="currentColor" 
                            viewBox="0 0 20 20" 
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path 
                              fillRule="evenodd" 
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" 
                              clipRule="evenodd" 
                            />
                          </svg>
                          {new Date(item.date).toLocaleDateString()} à {new Date(item.date).toLocaleTimeString()}
                        </div>
                      </div>
                      <div 
                        className="ml-2 p-2 text-gray-400 hover:text-yellow-400 transition-colors"
                        title="Voir la fiche"
                      >
                        <svg 
                          className="w-5 h-5" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                          />
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" 
                          />
                        </svg>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-700 text-center">
                  <p className="text-sm text-gray-400">
                    {drawHistory.length} cardinal{drawHistory.length > 1 ? 's' : ''} sélectionné{drawHistory.length > 1 ? 's' : ''}
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PackOpener;
